import React, {ComponentType} from 'react';

import {Location} from 'history';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {withRouter, WithRouterProps} from 'react-router';

import {LinkPagination} from 'app/components/general/LinkPagination';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {StyledListDiv} from 'app/general/bootstrap/utils';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {ContentFetchProps} from 'app/general/datafetchers/builders';
import {LoadingList, LoadingListProps} from 'app/general/loading/LoadingList';
import {StoredResponseType} from 'app/utils/redux_v2';

const wrapInNoResultsIfString = (content: JSX.Element | string | null) =>
  typeof content === 'string' ? <NoResultsMessage head={content} /> : content;

type UserRoleType = {
  isManager: boolean;
  isSuperManager: boolean;
  isTeamMember: boolean;
};

export interface GenericPaginatedListCommonProps<
  PR,
  CSP extends Record<string, unknown> = Record<string, unknown>
> {
  noItemsMessage: (
    arg0: UserRoleType,
    arg1: typeof NoResultsMessage,
    arg2: CSP | Record<string, unknown>
  ) => JSX.Element | string | null;
  paramsBuilder: (router: {location: Location<any>; params: any}) => PR;
  pageParamName?: string;
  fetchPageName?: string;
  loadingListProps?: LoadingListProps;
}

type ItemType = {
  id?: string | number;
  url?: string;
  html_url?: string;
};

interface Props<
  IT,
  PR,
  CSP extends Record<string, unknown>,
  CDP extends Record<string, unknown>
> extends GenericPaginatedListCommonProps<PR, CSP>,
    WithRouterProps {
  ItemComponent: ComponentType<
    CSP &
      CDP & {
        object: IT;
        refetchList: () => Promise<any>;
      }
  >;
  FetcherComponent: ComponentType<ContentFetchProps<IT, PR>>;
  onFetch?: (arg0: Array<StoredResponseType<IT>>) => void;
  children?: (
    response: StoredResponseType<IT>,
    fetchData: () => Promise<any>,
    list: JSX.Element | null
  ) => JSX.Element | null;
  borderTop?: boolean;
  customProps?: (arg0: DefaultRootState) => CSP;
  customDispatchProps?: (arg0: {
    dispatch: any;
    fetchData: () => Promise<any>;
  }) => CDP;
  resultsFormatter?: (arg0: Array<IT>) => Array<IT>;
  WrapperComponent?: (arg0: {
    children: React.ReactNode;
    refetchList: () => Promise<any>;
  }) => JSX.Element;
  itemsCounterText?: (count: number) => React.ReactNode;
}

const BaseGenericPaginatedList = withRouter(
  <
    IT extends ItemType,
    PR,
    CSP extends Record<string, unknown>,
    CDP extends Record<string, unknown>
  >({
    ItemComponent,
    FetcherComponent,
    paramsBuilder,
    onFetch,
    children,
    noItemsMessage,
    borderTop,
    loadingListProps = {},
    pageParamName = 'page',
    fetchPageName = 'page',
    customProps,
    customDispatchProps,
    resultsFormatter,
    WrapperComponent,
    location,
    params,
    itemsCounterText,
  }: Props<IT, PR, CSP, CDP>): JSX.Element | null => {
    const stateProps: {
      userPerms: UserRoleType;
      customProps: CSP;
    } = useSelector((state) => {
      const {isTeamMember, isManager, isSuperManager} = getCurrentUserPerms(
        CurrentUserSelector(state).data
      );
      return {
        userPerms: {
          isManager,
          isSuperManager,
          isTeamMember,
        },
        customProps:
          customProps != null ? customProps(state) : Object.freeze({} as CSP),
      };
    });
    const dispatch = useDispatch();
    return (
      <FetcherComponent
        params={{
          [fetchPageName]: location.query[pageParamName] as string,
          ...paramsBuilder({
            location,
            params,
          }),
        }}
        onFetch={onFetch}
      >
        {(response, fetchData) => {
          const results =
            resultsFormatter != null
              ? resultsFormatter(response.results)
              : response.results;

          const formattedResults: React.ReactNode = (
            <>
              {itemsCounterText != null ? (
                <DashboardItemsCounter
                  text={itemsCounterText(response.count)}
                />
              ) : null}
              {results.map((object) => (
                <ItemComponent
                  refetchList={fetchData}
                  key={object.url || object.html_url || object.id}
                  {...{
                    ...stateProps.customProps,
                    ...(customDispatchProps != null
                      ? customDispatchProps({
                          dispatch,
                          fetchData,
                        })
                      : Object.freeze({} as CDP)),
                  }}
                  object={object}
                />
              ))}
            </>
          );

          const list = response.loading ? (
            <LoadingList {...loadingListProps} />
          ) : results.length === 0 ? (
            wrapInNoResultsIfString(
              noItemsMessage(
                stateProps.userPerms,
                NoResultsMessage,
                stateProps.customProps
              )
            )
          ) : (
            <StyledListDiv className={borderTop === true ? 'border-top' : ''}>
              {WrapperComponent != null ? (
                <WrapperComponent refetchList={fetchData}>
                  {formattedResults}
                </WrapperComponent>
              ) : (
                formattedResults
              )}
              <div className="mt-4">
                <LinkPagination
                  response={response}
                  page={location.query[pageParamName]}
                  pageParamName={pageParamName}
                />
              </div>
            </StyledListDiv>
          );
          return children != null ? children(response, fetchData, list) : list;
        }}
      </FetcherComponent>
    );
  }
);

/*
  all this dancing is required to keep the generic types after using withRouter
  as the return type of withRouter isn't generic
*/
export const GenericPaginatedList = BaseGenericPaginatedList as <
  IT extends ItemType,
  PR,
  CSP extends Record<string, unknown>,
  CDP extends Record<string, unknown>
>(
  props: Omit<Props<IT, PR, CSP, CDP>, keyof WithRouterProps>
) => JSX.Element;
